import * as React from 'react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import LoadingModal from '@components/Modal/LoadingModal'
import WarningModal from '@components/Modal/WarningModal'
import { ModalSelectSkillProduct } from '@features/skillsTutor/components'
import {
  ModalSelectSkillProductProps as MultiSelectSkillProductProps,
  ProductOption,
} from '@features/skillsTutor/components'
import { POSTSLM } from '@features/skillsTutor/api'
import { useMutation } from '@apollo/react-hooks'
import { SkillsTutorProduct } from '@features/skillsTutor/types'
import {
  getBasicOrInterSkillsTutors,
  getSkillsTutorLabel,
  SkillsTutorWithLevel,
  diffSkillsTutorAssignments,
  getSkillsTutorCourseKey,
} from '@features/skillsTutor/utils'
import { StudentLanguageMaterial } from '@interfaces/StudentLanguageMaterial'
import { StoreProps } from '@interfaces/StoreState'
import { SKILLS_TUTOR_STATUS } from '@features/skillsTutor/constants/status'

export interface SelectSkillProductModalProps {
  open?: boolean
  skillsTutorOptions?: SkillsTutorProduct[]
  onClose?: () => void
  onUpdated?: (params: {
    expectedAssignIds: string[]
    expectedUnassignIds: string[]
  }) => Promise<{ phase1Success: boolean; phase2Success: boolean; phase3Success: boolean }>
  isRefreshing?: boolean
  refreshPhase?: 0 | 1 | 2 | 3
  remainingSwitches?: number
  assignedMaterials?: StudentLanguageMaterial[]
  completedMaterials?: StudentLanguageMaterial[]
  studentProfileId?: string
  studentLanguageProfileId?: string
  activeLearningLanguage?: string
}

export const SelectSkillProductsModal: React.FC<SelectSkillProductModalProps> = ({
  open: manualOpen,
  skillsTutorOptions,
  onClose = () => {},
  onUpdated,
  isRefreshing,
  refreshPhase,
  remainingSwitches,
  assignedMaterials,
  completedMaterials,
  studentProfileId,
  studentLanguageProfileId,
  activeLearningLanguage = 'English',
}) => {
  const intl = useIntl()
  const portalLocale = useSelector(({ intl }: StoreProps) => intl.locale)
  const [open, setOpen] = useState(!!manualOpen)
  const [loading, setLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [pollingLoading, setPollingLoading] = useState(false)
  const [options, setOptions] = useState<ProductOption[]>()
  const [errorMessage, setErrorMessage] = useState('')
  const [error, setError] = useState(false)
  const isPreviouslySkillsSelected =
    (assignedMaterials && assignedMaterials?.length > 0) || (completedMaterials && completedMaterials?.length > 0)

  const basicOrInterSkillsTutors = getBasicOrInterSkillsTutors(skillsTutorOptions || [])

  const buildOptionsFromSkillsTutors = (
    skillsTutors: SkillsTutorWithLevel[],
    completed: StudentLanguageMaterial[] | undefined
  ): ProductOption[] => {
    return skillsTutors.map((opt, index) => {
      const value = opt?.ProductInfo?.ProductId
      const isDisabled = completed?.some((mat) => mat.MaterialId === value) || false
      const skillsTutorLabel = getSkillsTutorLabel(opt?.ProductInfo?.Name, opt.level)

      // Get approved description with locale fallback
      const courseKey = getSkillsTutorCourseKey({
        productId: value,
      })

      return {
        value,
        label: isDisabled ? `${skillsTutorLabel} ✅` : skillsTutorLabel,
        key: value,
        level: opt.level,
        index,
        isDisabled,
        slub: courseKey,
      }
    })
  }

  const [postSLM] = useMutation(POSTSLM)

  useEffect(() => {
    if (basicOrInterSkillsTutors && basicOrInterSkillsTutors.length >= 1) {
      setOptions(buildOptionsFromSkillsTutors(basicOrInterSkillsTutors, completedMaterials))
    }
  }, [skillsTutorOptions, completedMaterials])

  useEffect(() => {
    typeof manualOpen === 'boolean' && setOpen(manualOpen && !!options?.length)
  }, [manualOpen, options?.length])

  useEffect(() => {
    setLoading(!!manualOpen && !open)
  }, [manualOpen, open])

  const multiOnButtonClick = async (selectedSkillProducts: ProductOption[]) => {
    try {
      setUpdateLoading(true)

      const selectedIds = selectedSkillProducts.map((product) => product.value)
      const { toAssign, toUnassign } = diffSkillsTutorAssignments(selectedIds, assignedMaterials || [])

      if (toUnassign?.length === 0 && toAssign?.length === 0) {
        setOpen(false)
        onClose()
        setUpdateLoading(false)
        return
      }

      // Unassign previously assigned materials
      if (toUnassign && toUnassign.length > 0) {
        const removeResult = await Promise.all(
          toUnassign.map((materialId) =>
            postSLM({
              variables: {
                postSLMInput: {
                  studentLanguageProfileId: studentLanguageProfileId,
                  materialId,
                  status: SKILLS_TUTOR_STATUS.UNASSIGNED,
                },
              },
            })
          )
        )

        const allMaterialsRemoveSuccess = removeResult.every(
          (result) => result?.data?.postSLM?.ServiceStatus?.StatusCode === 200
        )

        if (!allMaterialsRemoveSuccess) {
          throw new Error('Failed to unassign one or more previous materials')
        }
      }

      // Assign new materials
      if (toAssign && toAssign.length > 0) {
        const putResult = await Promise.all(
          toAssign.map((materialId) =>
            postSLM({
              variables: {
                postSLMInput: {
                  studentLanguageProfileId: studentLanguageProfileId,
                  materialId,
                  status: SKILLS_TUTOR_STATUS.ASSIGNED,
                },
              },
            })
          )
        )

        const allMaterialsPutSuccess = putResult.every(
          (result) => result?.data?.postSLM?.ServiceStatus?.StatusCode === 200
        )

        if (!allMaterialsPutSuccess) {
          throw new Error('Failed to assign one or more new materials')
        }
      }

      // Refresh data (includes two-phase polling) before closing modal
      setUpdateLoading(false)
      setPollingLoading(true)
      try {
        await onUpdated?.({
          expectedAssignIds: toAssign || [],
          expectedUnassignIds: toUnassign || [],
        })
      } finally {
        setPollingLoading(false)
      }

      setOpen(false)
      onClose()
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      setErrorMessage(error.message || 'An error occurred while updating the course.')
      setUpdateLoading(false)
      setPollingLoading(false)
      setError(true)
    }
  }

  const props: MultiSelectSkillProductProps = {
    open: open,
    options,
    initialSelected: assignedMaterials?.map((material) => material?.MaterialId) as string[],
    onClose: () => {
      setOpen(false)
      onClose()
    },
    remainingSwitches: remainingSwitches,
    title: isPreviouslySkillsSelected ? (
      <FormattedMessage
        id="Select a new {name}"
        defaultMessage="Select a new {name}"
        values={{ name: 'MyBerlitz\u00A0Skills\u00A0Tutor' }}
      />
    ) : (
      <FormattedMessage
        id="Welcome to {name}!"
        defaultMessage="Welcome to {name}!"
        values={{ name: 'MyBerlitz\u00A0Skills\u00A0Tutor' }}
      />
    ),
    buttonText: isPreviouslySkillsSelected ? (
      <FormattedMessage id="Update Learning Path" defaultMessage="Update Learning Path" />
    ) : (
      <FormattedMessage id="Add to Learning Path" defaultMessage="Add to Learning Path" />
    ),
    selectLabel: intl.formatMessage({
      id: 'Select 1 or 2 Tutors that best fits your learning goal.',
      defaultMessage: 'Select 1 or 2 Tutors that best fits your learning goal.',
    }),
    maxLimit: 2,
    onButtonClick: multiOnButtonClick,
  }

  if (loading) {
    return (
      <LoadingModal
        open={loading}
        onClose={onClose || (() => {})}
        title={intl.formatMessage({
          id: 'LoadingSkillsTutorOptions',
          defaultMessage: 'Loading Skills Tutor options…',
        })}
      />
    )
  }

  if (updateLoading) {
    return (
      <LoadingModal
        open={updateLoading}
        onClose={onClose || (() => {})}
        title={intl.formatMessage({
          id: 'Please wait a moment',
          defaultMessage: 'Please wait a moment',
        })}
        subtitle={intl.formatMessage({
          id: 'UpdatingSkillsTutorSelection',
          defaultMessage: 'We are processing your changes. Please wait…',
        })}
      />
    )
  }

  if (pollingLoading) {
    const getSubtitle = () => {
      switch (refreshPhase) {
        case 1:
          return 'Updating your Skills Tutor selection…'
        case 2:
          return 'Updating your learning path…'
        case 3:
          return 'Finalizing updates…'
        default:
          return 'Processing…'
      }
    }

    return (
      <LoadingModal
        open={pollingLoading}
        onClose={onClose || (() => {})}
        title={intl.formatMessage({
          id: 'Please wait a moment',
          defaultMessage: 'Please wait a moment',
        })}
        subtitle={getSubtitle()}
      />
    )
  }

  if (error) {
    return (
      <WarningModal
        open={error}
        onClose={() => {
          setError(false)
          onClose()
        }}
        onCancel={() => setError(false)}
        anApiError
        description={errorMessage}
        onProceed={() => setError(false)}
        proceedLabel={intl.formatMessage({ id: 'OK', defaultMessage: 'OK' })}
      />
    )
  }

  return <ModalSelectSkillProduct {...props} />
}
